/**
 * Alarm toasts, and the notification bell that keeps what you missed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THEY APPEAR, AND WHY IT IS NOT THE TOP RIGHT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Bottom right. The top right of this console is where the site selector, the compliance
 * chip, the bell and the account menu live, and a toast stack there covers the controls an
 * operator reaches for WHEN AN ALARM ARRIVES — including the bell that would let them see
 * the rest of them. An interruption that hides the response to itself is the wrong
 * interruption.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ACCESSIBILITY IS NOT OPTIONAL HERE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This is a live region, so a screen reader has to be told. `role="log"` with
 * `aria-live="polite"` announces arrivals without cutting off whatever is being read —
 * `assertive` would interrupt mid-sentence, and on a chattering device that is unusable.
 *
 * The countdown is paused on hover and on focus. A toast that vanishes while somebody is
 * reading it, or while a keyboard user is tabbing to its dismiss button, is a toast that
 * cannot be acted on.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import { isRuleIdentity, liveAlarmLabel } from './alarm-readable';
import { useRuleNameOf } from '@/lib/api/rules';
import { TOAST_MS, useAlarmFeed, type ToastItem } from '@/app/alarm-feed';
import type { LiveAlarm } from '@/lib/api/events';
import { useMarkNotificationsRead, useNotifications, type NotificationRow } from '@/lib/api/notifications';
import { severityWord } from '@/modules/alarms/severity';
import { SeverityMark } from './SeverityMark';
import { SimulatedPill } from './SimulatedPill';
import { operatorDeviceName, toastShown } from './toast-scope';
import { useSiteContext } from '@/app/site-context-value';
import { useOrgContext } from '@/app/org-context-value';
import { useAllDevices } from '@/lib/api/onboarding';

/* --------------------------------------------------------------- severity */

/**
 * Tone by severity, with the EVENT overriding it.
 *
 * A cleared critical is good news and must not be painted like a critical fault — the
 * colour has to say what happened, not only what kind of thing it was.
 */
function tone(a: LiveAlarm): { colour: string; tint: string; icon: 'ic-alert' | 'ic-check' | 'ic-info' } {
  // Cleared is not a verdict — green means pass, and a cleared alarm is simply over.
  if (a.event === 'closed') return { colour: 'var(--muted-fg)', tint: 'var(--muted)', icon: 'ic-check' };
  const s = a.severity.toLowerCase();
  if (s.includes('critical')) return { colour: 'var(--danger)', tint: 'rgb(var(--danger-rgb) / 16%)', icon: 'ic-alert' };
  if (s.includes('major') || s.includes('warn')) return { colour: 'var(--warn)', tint: 'rgb(var(--warn-rgb) / 16%)', icon: 'ic-alert' };
  return { colour: 'var(--info)', tint: 'rgb(var(--info-rgb) / 16%)', icon: 'ic-info' };
}

const VERB: Record<LiveAlarm['event'], string> = {
  opened: 'raised',
  reopened: 're-raised',
  closed: 'cleared',
  changed: 'updated',
};

function ago(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

/* ----------------------------------------------------------------- toasts */

export function AlarmToasts({ onOpenAlarm }: { onOpenAlarm: (id: string) => void }): ReactNode {
  const all = useAlarmFeed((s) => s.toasts);
  const dismiss = useAlarmFeed((s) => s.dismiss);
  /* A simulated site's toasts only over a demo site (`toast-scope.ts`); the rest are let go at once,
     so they never wait unseen behind the ones shown. */
  const { sites, selected } = useSiteContext();
  const org = useOrgContext();
  const siteOf = useMemo(() => new Map(sites.map((s) => [s.id, s])), [sites]);
  const toasts = useMemo(
    () => all.filter((t) => toastShown(siteOf.get(t.site), selected, org.selectedId)),
    [all, siteOf, selected, org.selectedId],
  );
  useEffect(() => {
    for (const t of all) if (!toastShown(siteOf.get(t.site), selected, org.selectedId)) dismiss(t.key);
  }, [all, siteOf, selected, org.selectedId, dismiss]);
  const ruleName = useRuleNameOf(toasts.some((t) => isRuleIdentity(t.name)));

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      style={{ width: 380, maxWidth: 'calc(100vw / var(--app-zoom, 1) - 32px)' }}
      role="log"
      aria-live="polite"
      aria-label="Alarm notifications"
    >
      {/* Newest at the BOTTOM, nearest the corner: the stack grows upward, so the position
          of the newest item does not move as others arrive. A stack that grows downward
          moves the thing you are reading. */}
      {/*
        `dismiss` IS PASSED WHOLE, not wrapped. `onDismiss={() => dismiss(t.key)}` builds a
        fresh closure on every render of this component, and that closure is in the child's
        effect dependency array — so every store update (any toast arriving, any toast
        leaving) cleared and re-armed the timeout on EVERY visible toast. On a device
        chattering faster than TOAST_MS, nothing ever auto-dismissed and the stack grew until
        it ran off the screen. There is no React Compiler in this build to memoise it away.
        The store action is stable, so the child's effect now depends only on things that
        genuinely change.
      */}
      {toasts.map((t) => (
        <Toast key={t.key} item={t} site={siteOf.get(t.site)} onDismiss={dismiss} onOpen={onOpenAlarm} ruleName={ruleName} />
      ))}
    </div>
  );
}

function Toast({
  item,
  site,
  onDismiss,
  onOpen,
  ruleName,
}: {
  item: ToastItem;
  /** The alarm's site, from the viewer's site list; absent only for one outside it. */
  site: { id: string; name: string; organisationId: string; simulated?: boolean } | undefined;
  /** The store action itself — stable across renders. See the note at the call site. */
  onDismiss: (key: string) => void;
  onOpen: (id: string) => void;
  ruleName: (id: string) => string | undefined;
}): ReactNode {
  const [paused, setPaused] = useState(false);
  const t = tone(item);
  /* The device by the console's own name ("DC system 1"), from the site's device list (cached). */
  const devicesQ = useAllDevices(item.site || null);
  const device = useMemo(() => operatorDeviceName(item.device, devicesQ.data?.data ?? []), [item.device, devicesQ.data]);
  const life = TOAST_MS[item.event];

  /*
   * The timer restarts whenever `paused` goes false, which is the intended behaviour: a
   * toast the operator hovered gets its full life again when they move away, rather than
   * disappearing the instant the cursor leaves.
   */
  useEffect(() => {
    if (paused || life <= 0) return;
    const id = setTimeout(() => onDismiss(item.key), life);
    return () => clearTimeout(id);
  }, [paused, life, item.key, onDismiss]);

  return (
    <div
      className="lm-overlay lm-slidein pointer-events-auto flex items-start gap-[10px] p-[12px_13px]"
      style={{ borderLeft: `3px solid ${t.colour}` }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span
        className="mt-[1px] flex size-[22px] shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
        style={{ background: t.tint, color: t.colour }}
      >
        {item.event === 'closed' ? <Icon name={t.icon} size={13} /> : <SeverityMark severity={item.severity} size={11} />}
      </span>

      {/* The body opens the alarm — a toast you can read but not follow is half a notification.
          Severity is named in ink; the colour stays on the mark and the frame. */}
      <button
        type="button"
        onClick={() => {
          onOpen(item.id);
          onDismiss(item.key);
        }}
        className="min-w-0 flex-1 cursor-pointer text-left"
        aria-label={`Open alarm: ${liveAlarmLabel(item, ruleName)} on ${device}${site ? `, ${site.name}${site.simulated ? ', simulated' : ''}` : ''}`}
      >
        <span className="flex items-baseline gap-2">
          <span className="lm-t-sm font-bold capitalize text-[var(--fg)]">
            {severityWord(item.severity)} {VERB[item.event]}
          </span>
          <span className="lm-num lm-t-micro text-[var(--muted-fg)]">{ago(item.receivedAt)}</span>
        </span>
        <span className="lm-t-sm mt-[2px] block truncate text-[var(--fg)]">{liveAlarmLabel(item, ruleName)}</span>
        <span className="lm-t-micro flex min-w-0 items-center gap-[6px] text-[var(--muted-fg)]">
          <span className="truncate">
            {device}
            {site ? ` · ${site.name}` : ''}
          </span>
          <SimulatedPill simulated={site?.simulated} />
        </span>
      </button>

      <button
        type="button"
        onClick={() => onDismiss(item.key)}
        className="lm-hov shrink-0 rounded-[var(--radius-sm)] p-1"
        style={{ color: 'var(--muted-fg)' }}
        aria-label="Dismiss"
      >
        <Icon name="ic-x" size={13} />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------- bell */

/**
 * The header bell: an unseen count, and the log behind it.
 *
 * The count is UNSEEN rather than unread — it clears when the panel is opened, not when
 * each row is looked at. Per-row read state on a notification log is a promise the product
 * would then have to keep across devices and sessions, and nobody wants it: the question
 * the badge answers is "has anything happened since I last looked".
 */
export function AlarmBell({
  onOpenAlarm,
  onOpenWorkOrder,
}: {
  onOpenAlarm: (id: string) => void;
  /** A notification about a work order opens it (`modules/workorders/focus.ts`). */
  onOpenWorkOrder: (id: string) => void;
}): ReactNode {
  const { recent, unseen: unseenAlarms, markSeen, stream } = useAlarmFeed();
  /*
   * TWO KINDS OF THING IN ONE BELL, COUNTED DIFFERENTLY ON PURPOSE.
   *
   * Alarm events are a session log about the plant — "unseen" clears on opening, as the note
   * above argues. Notifications (migration 0087) are addressed to THIS PERSON and stored, so they
   * carry real read state: one stays unread until it is opened or marked read, on every device,
   * because "you were given a work order" must not be cleared by glancing at the bell.
   */
  const inbox = useNotifications();
  const markRead = useMarkNotificationsRead();
  const notices = inbox.data?.data ?? [];
  const unreadNotices = inbox.data?.unread ?? 0;
  const unseen = unseenAlarms + unreadNotices;
  const [open, setOpen] = useState(false);
  const ruleName = useRuleNameOf(open && recent.some((a) => isRuleIdentity(a.name)));
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = useMemo(() => {
    if (stream === 'offline') return 'Alarm stream offline — the board still refreshes on its own';
    if (stream === 'connecting') return 'Connecting to the alarm stream…';
    const parts = [
      ...(unreadNotices > 0 ? [`${unreadNotices} for you`] : []),
      ...(unseenAlarms > 0 ? [`${unseenAlarms} new alarm event(s)`] : []),
    ];
    return parts.length > 0 ? parts.join(', ') : 'Live — nothing new';
  }, [stream, unseenAlarms, unreadNotices]);

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) markSeen();
        }}
        className="lm-hov relative flex items-center justify-center rounded-[var(--radius-sm)]"
        style={{
          width: 'var(--header-ctl-h)',
          height: 'var(--header-ctl-h)',
          color: 'var(--muted-fg)',
        }}
        aria-label={label}
        title={label}
        aria-expanded={open}
      >
        <Icon name="ic-bell" size={16} />

        {unseen > 0 && (
          <span
            className="lm-num absolute -right-[1px] -top-[1px] flex min-w-[16px] items-center justify-center rounded-[var(--radius-pill)] px-[3px] text-[9.5px] font-bold"
            style={{ background: 'var(--danger)', color: 'var(--bg)' }}
          >
            {unseen > 99 ? '99+' : unseen}
          </span>
        )}

        {/*
          * THE STREAM STATE IS SHOWN, and that matters more than it looks.
          *
          * If the stream is down the operator is looking at a bell that will never light
          * up, and the honest thing is to say so — otherwise silence means "nothing is
          * wrong" when it means "we are not being told". The dot is the only place in the
          * UI that distinguishes the two.
          */}
        {unseen === 0 && (
          <span
            className="absolute bottom-[6px] right-[6px] size-[5px] rounded-full"
            style={{
              background:
                stream === 'live'
                  ? 'var(--muted-fg)'
                  : stream === 'connecting'
                    ? 'var(--warn)'
                    : 'var(--danger)',
            }}
          />
        )}
      </button>

      {open && (
        <div
          className="lm-overlay absolute right-0 top-[calc(100%+8px)] z-50 overflow-hidden"
          style={{ width: 380, maxWidth: 'calc(100vw / var(--app-zoom, 1) - 24px)' }}
        >
          {/*
            * FOR YOU — first, because it is the only part of the bell addressed to the reader by
            * name. Hidden when there has never been one, rather than an empty heading on every
            * open for the many people who are never assigned work.
            */}
          {notices.length > 0 && (
            <div style={{ borderBottom: '1px solid var(--glass-border)' }}>
              <div className="flex items-center justify-between px-[13px] pb-[4px] pt-[8px]">
                <span className="lm-t-micro font-semibold text-[var(--muted-fg)]">
                  For you
                  {unreadNotices > 0 && (
                    <span className="lm-num ml-[6px] text-[var(--fg)]">{unreadNotices} unread</span>
                  )}
                </span>
                {unreadNotices > 0 && (
                  <button
                    type="button"
                    disabled={markRead.isPending}
                    onClick={() => markRead.mutate(undefined)}
                    className="lm-hov lm-t-micro rounded-[var(--radius-sm)] px-[6px] py-[2px] font-semibold text-[var(--muted-fg)]"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-[220px] overflow-y-auto">
                {notices.slice(0, 8).map((n) => (
                  <NoticeRow
                    key={n.id}
                    n={n}
                    onOpen={() => {
                      setOpen(false);
                      if (n.readAt === null) markRead.mutate([n.id]);
                      if (n.entityType === 'work_order') onOpenWorkOrder(n.entityId);
                      else if (n.entityType === 'alarm') onOpenAlarm(n.entityId);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          <div
            className="flex items-center justify-between px-[13px] py-[9px]"
            style={{ borderBottom: '1px solid var(--glass-border)' }}
          >
            <span className="lm-t-sm font-semibold text-[var(--fg)]">Recent alarm activity</span>
            {/* Live is the normal state and gets no colour; only a stream that is NOT live marks
                itself, on the dot — never the word. */}
            <span className="lm-t-micro flex items-center gap-1.5 text-[var(--muted-fg)]">
              <span
                className="size-[6px] rounded-full"
                style={{
                  background:
                    stream === 'live' ? 'var(--muted-fg)' : stream === 'connecting' ? 'var(--warn)' : 'var(--danger)',
                }}
              />
              {stream}
            </span>
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {recent.length === 0 ? (
              <p className="px-[13px] py-[14px] text-[12px]" style={{ color: 'var(--muted-fg)' }}>
                {stream === 'live'
                  ? 'Nothing since this page was opened. This log starts empty on every load — it is what has happened while you were watching, not the alarm history.'
                  : 'Not connected, so nothing is being pushed. The Alarms page still refreshes on its own.'}
              </p>
            ) : (
              recent.map((a, i) => {
                const t = tone(a);
                return (
                  <button
                    type="button"
                    key={`${a.id}-${a.event}-${a.receivedAt}-${i}`}
                    onClick={() => {
                      setOpen(false);
                      onOpenAlarm(a.id);
                    }}
                    className="lm-hov flex w-full items-start gap-[9px] px-[13px] py-[8px] text-left"
                    style={{ borderBottom: '1px solid var(--glass-border)' }}
                  >
                    <span
                      className="mt-[5px] size-[7px] shrink-0 rounded-full"
                      style={{ background: t.colour }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="lm-t-sm block truncate text-[var(--fg)]">{liveAlarmLabel(a, ruleName)}</span>
                      <span className="lm-t-micro block truncate text-[var(--muted-fg)]">
                        {a.device} · {VERB[a.event]} · {ago(a.receivedAt)}
                      </span>
                    </span>
                    <Icon name="ic-chevright" size={12} />
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** One notification in the bell. Unread is bold with a brand dot; read is plain — never a colour that means a state. */
function NoticeRow({ n, onOpen }: { n: NotificationRow; onOpen: () => void }): ReactNode {
  const unread = n.readAt === null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="lm-hov flex w-full items-start gap-[9px] px-[13px] py-[7px] text-left"
    >
      <span
        aria-hidden
        className={`mt-[6px] size-[7px] shrink-0 rounded-full ${unread ? 'bg-[var(--brand)]' : 'bg-transparent'}`}
      />
      <span className="min-w-0 flex-1">
        <span className={`lm-t-sm block truncate text-[var(--fg)] ${unread ? 'font-semibold' : ''}`}>
          {n.title}
        </span>
        {/* Truncated to one line; the whole of it — a block reason can be a sentence — on hover. */}
        <span className="lm-t-micro block truncate text-[var(--muted-fg)]" title={n.body ?? undefined}>
          {n.body ? `${n.body} · ` : ''}
          {ago(Date.parse(n.createdAt))}
        </span>
      </span>
      {unread && <span className="sr-only">unread</span>}
      <Icon name="ic-chevright" size={12} />
    </button>
  );
}
